import React, { useEffect, useState } from "react";
import { CloudOff, RefreshCw, Cloud, Check } from "lucide-react";
import { isOnline, getQueueLength, subscribeQueue, flushQueue } from "@/utils/offlineQueue";
import { toast } from "sonner";

/**
 * OfflineIndicator — sticky pill in the top-right corner that shows the
 * current connectivity status and the number of queued mutations.
 * Click to manually trigger a sync attempt.
 */
export default function OfflineIndicator() {
  const [online, setOnline] = useState(isOnline());
  const [queueLen, setQueueLen] = useState(getQueueLength());
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const onOnline = () => {
      setOnline(true);
      toast.success("Connexion rétablie · synchronisation en cours…", { duration: 4000 });
    };
    const onOffline = () => {
      setOnline(false);
      toast.warning("Mode hors-ligne · vos saisies sont enregistrées localement", { duration: 5000 });
    };
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    const unsub = subscribeQueue(setQueueLen);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      unsub();
    };
  }, []);

  const sync = async () => {
    if (!online) {
      toast.error("Pas de connexion · sync impossible pour l'instant");
      return;
    }
    setSyncing(true);
    try {
      const res = await flushQueue();
      if (res.flushed > 0) {
        toast.success(`${res.flushed} élément${res.flushed > 1 ? "s" : ""} synchronisé${res.flushed > 1 ? "s" : ""}`);
      } else if (res.remaining === 0) {
        toast.success("Tout est à jour");
      }
    } finally {
      setSyncing(false);
    }
  };

  // Hide entirely when online with no pending items AND we're not in test mode
  const forced = typeof window !== "undefined" && window.__rs_force_offline === true;
  if (online && queueLen === 0 && !forced) {
    // Render a tiny passive indicator so users always have visual feedback
    return (
      <div
        data-testid="offline-indicator-online"
        className="fixed top-2 right-2 z-50 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 text-[10px] font-medium  pointer-events-none"
        title="Connecté · données synchronisées"
      >
        <Cloud className="h-3 w-3" />
        <span>En ligne</span>
      </div>
    );
  }

  const palette = !online
    ? "bg-amber-500 text-white border-amber-600"
    : queueLen > 0
    ? "bg-blue-600 text-white border-blue-700"
    : "bg-emerald-500 text-white border-emerald-600";

  return (
    <button
      type="button"
      onClick={sync}
      disabled={syncing}
      data-testid="offline-indicator"
      className={`fixed top-2 right-2 z-50 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 text-xs font-semibold shadow-lg transition ${palette} ${syncing ? "" : "hover:scale-105"}`}
      title={!online ? "Vous êtes hors-ligne. Cliquez pour réessayer." : `${queueLen} élément(s) à synchroniser. Cliquez pour forcer.`}
    >
      {!online ? (
        <>
          <CloudOff className="h-3.5 w-3.5" />
          <span>Hors-ligne{queueLen > 0 ? ` · ${queueLen}` : ""}</span>
        </>
      ) : queueLen > 0 ? (
        <>
          <RefreshCw className={`h-3.5 w-3.5 ${syncing ? "animate-spin" : ""}`} />
          <span>{queueLen} en attente</span>
        </>
      ) : (
        <>
          <Check className="h-3.5 w-3.5" />
          <span>Sync OK</span>
        </>
      )}
    </button>
  );
}
